import { Injectable, OnDestroy } from '@angular/core';
import { loginStoreActions, relationshipStoreActions, RootStoreState } from '@app/root-store';
import { loaderTypes } from '@app/shared/component/loader/loader';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { finalize, map, take, takeUntil } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { AmplifyHttpClient } from '../auth/http.amplify-httpclient';
import { GraphQLService } from '../graphql/graphql.service';
import * as loginSelectors from '../../../root-store/login-store/login.selector';

import { environment } from '@env/environment';
import { fetchAuthSession, fetchUserAttributes } from '@aws-amplify/auth';
import { LoaderService } from '../loader/loader.service';
@Injectable({
  providedIn: 'root',
})
export class UserConnectionService implements OnDestroy {
  currentUserDetails: any;
  private worker: Worker | null = null;
  private activeRequests: Map<string, Promise<any>> = new Map();
  private destroy$ = new Subject<void>();

  constructor(
    private readonly httpService: AmplifyHttpClient,
    private store$: Store<RootStoreState.State>,
    private readonly graphqlService: GraphQLService,
    private authService: AuthService,
    private store: Store,
    private loaderService: LoaderService
  ) {
    // FIXED: Using takeUntil to properly cleanup subscription
    this.store.pipe(select(loginSelectors.getCurrentUserFullDetails), takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.currentUserDetails = result;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this._connections.complete();
    this.activeRequests.clear();
  }

  private _connections = new BehaviorSubject<Array<any>>([]);
  public connections$ = this._connections.asObservable();
  public set connections(value: any) {
    this._connections.next(value);
  }
  /**
   * userConnection method for get connection of loggedin users.
   * @param user_id
   * @return Observable
   */
  connectToUser(request, loader?: loaderTypes) {
    // const addConnectionRequest = {
    //   from_user_id: request.from_user_id ?? this.authService.user.id,
    //   to_user_id: request.requested_user_id,
    //   status: request.status ?? 'pending',
    //   relationship_id: request.relationship_id,
    // };

    const addConnectionRequest = {
      from_user_id: request.from_user_id ?? this.currentUserDetails.id,
      to_user_id: request.requested_user_id,
      status: request.status ?? 'pending',
      relationship_id: request.relationship_id,
    };

    return this.graphqlService.addConnection(addConnectionRequest, loader).pipe(
      map((response) => {
        if (response.status == 200) {
          this.store$.dispatch(new relationshipStoreActions.AllRelationships());
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  userConnectionUpdate(request) {
    //return this.httpService.post<any>(`api/connection/connect-user-update`, request).pipe(
    // request.from_user_id = this.authService.user.id;
    return this.graphqlService.updateConnection(request, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipUpdateNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-update-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteUserConnection(request) {
    return this.graphqlService.dropConnection(request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  sendRelationshipReminderNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-reminder-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipDeleteNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-delete-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipRequestDeclineNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-request-decline-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipRequestAcceptNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-request-accept-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipRequestNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-request-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  sendInviteReminderKinnectEmail(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-reminder`, request, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendInviteReminderOutsideKinnectEmail(request) {
    return this.httpService.post<any>(`api/user/send-invite-reminder-email`, request, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  deleteUserInviteOutsideKinnectEmail(request) {
    return this.httpService.post<any>(`api/user/delete-invite`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getMutualConnectionCount(id) {
    return this.httpService.get<any>(`api/connection/mutual-connections-count/${id}`).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getMutualConnectionList(id) {
    return this.httpService.post<any>(`api/connection/mutual-connections/${id}`, {}).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getMutualConnection(request, user_id) {
    return this.httpService.post<any>(`api/connection/mutual-connections/` + user_id, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  /**
   * @deprecated The method should not be used as it's been replaced with getallconnections with ngrx
   */
  getConnections(type = 'user', params = {}) {
    return this.graphqlService.getConnections('').pipe(
      map((response) => {
        if (response.status == 200) {
          this.connections = response.data;
          return response.data;
        } else {
          this.connections = response;
          return response;
        }
      })
    );
  }
  getSuggestionsWithLimit(count) {
    return this.httpService.get<any>(`api/connection/my-suggestion-limit/${count}`, loaderTypes.none).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getSuggestions(request) {
    return this.httpService.post<any>(`api/connection/suggestion`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getSuggestionsWithPagination(pageNo) {
    return this.httpService.get<any>(`api/connection/my-suggestion-all/${pageNo}`).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getConnectionRequest(request) {
    return this.httpService
      .get<any>(`api/connection/all-request`, { params: request })
      .pipe(
        map((response) => {
          return response;
        })
      )
      .toPromise()
      .then((data: any) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });
    // return this.httpService.get<any>(``, request).pipe(

    // )
  }
  getRequestConnectionStatus(request) {
    return this.httpService
      .get<any>(`api/connection/request-status-change`, { params: request })
      .pipe(
        map((response) => {
          return response;
        })
      )
      .toPromise()
      .then((data: any) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });
  }

  userConnectionUpdateStatus(request) {
    return this.httpService.post<any>(`api/connection/request-status-change`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getRelationshipChangeRequestStatus(request) {
    return this.httpService
      .get<any>(`api/connection/get-relationship-change-request-status`, { params: request })
      .pipe(
        map((response) => {
          return response;
        })
      )
      .toPromise()
      .then((data: any) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });
  }

  getConnectionsCount(user_id) {
    return this.httpService.get<any>(`api/connection/get-connection-count`, { params: user_id }).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  ignoreSuggestion(suggested_user_id) {
    return this.httpService.post<any>(`api/connection/suggestion-ignore`, { suggested_user_id: suggested_user_id }).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAllConnections(params) {
    return this.graphqlService.getConnections(params).pipe(
      map((response) => {
        if (response.status == 200) {
          this.connections = response.data;
          return response.data;
        } else {
          this.connections = response;
        }
      })
    );
  }

  checkUserIsAlreadyintheSystem(data) {
    return this.httpService.post<any>(`api/user/get-byEmail`, data, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  async getMySuggestions(): Promise<any> {
    const requestKey = 'getMySuggestions'; // Unique key for this request type

    // Check if there is an already active request
    if (this.activeRequests.has(requestKey)) {
      return this.activeRequests.get(requestKey)!;
    }

    // Create a new promise for the current request
    const requestPromise = new Promise(async (resolve, reject) => {
      if (typeof Worker !== 'undefined') {
        if (!this.worker) {
          this.worker = new Worker(new URL('./my-suggestion.worker', import.meta.url), { type: 'module' });
        }

        let data = {
          token: '',
          url: '',
        };

        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          data.token = `Bearer ${token}`;
          data.url = `${environment.apiUrl}/api/connection/my-suggestions`;

          this.worker.onmessage = ({ data }) => {
            const res = data.data;
            resolve(res);

            // Cleanup: Remove the active request after it's completed
            this.activeRequests.delete(requestKey);
          };

          this.worker.onerror = (error) => {
            reject(error);

            // Cleanup on error
            this.activeRequests.delete(requestKey);
          };

          this.worker.postMessage(data);
        } catch (error) {
          reject(error);
          this.activeRequests.delete(requestKey);
        }
      } else {
        // Browser does not support web workers, handle fallback logic
        // resolve(this.generateCreatePresignedUrl(null));
        reject(new Error('Web Workers are not supported in this browser.'));
        this.activeRequests.delete(requestKey);
      }
    });

    // Store the active request in the map
    this.activeRequests.set(requestKey, requestPromise);

    return requestPromise;
  }

  getMutualConnections(user_id) {
    return this.httpService.get<any>(`api/connection/mutual-connections/${user_id}`, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  mySuggestionRemove(suggested_user_id) {
    return this.httpService.post<any>(`api/connection/my-suggestions-ignore`, { suggested_user_list: suggested_user_id }).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  connectMultipleUsers(userIds: Array<string | number>, loader?: loaderTypes) {
    this.loaderService.show(loaderTypes.mainAnimation);
    const connectionObservables: Observable<any>[] = [];
    for (let i = 0; i < userIds.length; i++) {
      for (let j = i + 1; j < userIds.length; j++) {
        const from_user_id = userIds[i];
        const to_user_id = userIds[j];

        const connectionData = {
          relationship_name: undefined,
          from_user_id,
          requested_user_id: to_user_id,
          relationship_id: 118,
        };

        const obs$ = this.connectToUser(connectionData, loaderTypes.mainAnimation).pipe(
          take(1), // Take only the first emission
          catchError((err) => {
            console.error(`Error connecting ${from_user_id} -> ${to_user_id}`, err);
            return [null]; // Replace errored observable with null so forkJoin continues
          })
        );
        connectionObservables.push(obs$);
      }
    }

    if (connectionObservables.length === 0) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    return forkJoin(connectionObservables).pipe(
      finalize(() => {
        this.loaderService.hide();
      })
    );
  }
  connectSingleUserToOtherUsers(selectedUserId: string | number, userIds: Array<string | number>, loader?: loaderTypes) {
    this.loaderService.show(loaderTypes.mainAnimation);
    const connectionObservables: Observable<any>[] = [];
    for (let i = 0; i < userIds.length; i++) {
      const from_user_id = selectedUserId;
      const to_user_id = userIds[i];

      const connectionData = {
        relationship_name: undefined,
        from_user_id,
        requested_user_id: to_user_id,
        relationship_id: 118,
      };

      const obs$ = this.connectToUser(connectionData, loaderTypes.mainAnimation).pipe(
        take(1), // Take only the first emission
        catchError((err) => {
          console.error(`Error connecting ${from_user_id} -> ${to_user_id}`, err);
          return [null]; // Replace errored observable with null so forkJoin continues
        })
      );
      connectionObservables.push(obs$);
    }

    if (connectionObservables.length === 0) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    return forkJoin(connectionObservables).pipe(
      finalize(() => {
        this.loaderService.hide();
      })
    );
  }
  getRelationshipConnections(relationship_id: number) {
    return this.graphqlService.getConnections(relationship_id, loaderTypes.mainAnimation, true).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getUserInfo() {
    return this.httpService.get<any>(`api/user/connected-users-info`, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
}
